import React, { useEffect, useState } from 'react';
import {
    Alert,
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    IconButton,
    Stack,
    TextField,
    Typography,
} from '@mui/material';
import {
    Schedule as ScheduleIcon,
    Business as BusinessIcon,
    Support as SupportIcon,
    CalendarMonth as CalendarIcon,
    ArrowBack as ArrowBackIcon,
    ArrowForwardRounded as ArrowForwardIcon,
    CloseRounded as CloseIcon,
} from '@mui/icons-material';
import { config } from '../helpers/config';
import { buildContactSummary, sendMailRequest } from '../helpers/mailHelper';
import loggerHelper from '../helpers/loggerHelper';
import CalendarDialog from './CalendarDialog';

const ContactDialog = ({ open, onClose, serviceTitle }) => {
    const [calendarDialogOpen, setCalendarDialogOpen] = useState(false);
    const [selectedOption, setSelectedOption] = useState(null);
    const [submitting, setSubmitting] = useState(false);
    const [submitError, setSubmitError] = useState('');
    const [submitted, setSubmitted] = useState(false);
    const [form, setForm] = useState({
        customerName: '',
        customerEmail: '',
        phone: '',
        businessName: '',
        message: '',
    });
    const [errors, setErrors] = useState({});

    const options = [
        {
            label: 'Book Appointment',
            value: 'calendar',
            icon: <CalendarIcon />,
            description: 'Schedule a meeting directly through our calendar',
            isCalendar: true,
            calendarUrl: config.calendarUrl,
        },
        {
            label: 'Schedule Consultation',
            value: 'schedule',
            icon: <ScheduleIcon />,
            description: 'Book a personalized consultation with our AI experts',
            emailSubject: 'Consultation Request',
            defaultMessage: `I would like to schedule a consultation regarding ${serviceTitle}.\n\nBest time to reach me:\n\nProject details:`,
        },
        {
            label: 'Contact Sales',
            value: 'sales',
            icon: <BusinessIcon />,
            description: 'Discuss pricing and custom solutions for your business',
            emailSubject: 'Sales Inquiry',
            defaultMessage: `I'm interested in learning more about ${serviceTitle}.\n\nCompany size:\nBudget range:\nTimeline:\nSpecific requirements:`,
        },
        {
            label: 'Technical Support',
            value: 'support',
            icon: <SupportIcon />,
            description: 'Get technical assistance and implementation support',
            emailSubject: 'Technical Support',
            defaultMessage: `I need technical support for ${serviceTitle}.\n\nIssue description:\nSteps to reproduce:\nCurrent behavior:\nExpected behavior:`,
        },
    ];

    useEffect(() => {
        if (!open) {
            setSelectedOption(null);
            setSubmitting(false);
            setSubmitError('');
            setSubmitted(false);
            setErrors({});
            setForm({
                customerName: '',
                customerEmail: '',
                phone: '',
                businessName: '',
                message: '',
            });
        }
    }, [open]);

    const updateField = (name, value) => {
        setForm((prev) => ({ ...prev, [name]: value }));
        if (errors[name]) {
            setErrors((prev) => {
                const next = { ...prev };
                delete next[name];
                return next;
            });
        }
    };

    const handleOptionClick = (option) => {
        if (option.isCalendar && option.calendarUrl) {
            setCalendarDialogOpen(true);
            onClose();
            return;
        }

        setSelectedOption(option);
        setSubmitError('');
        setSubmitted(false);
        setForm((prev) => ({
            ...prev,
            message: option.defaultMessage || '',
        }));
    };

    const validateForm = () => {
        const nextErrors = {};
        if (!form.customerName.trim()) nextErrors.customerName = 'Name is required';
        if (!form.customerEmail.trim() && !form.phone.trim()) {
            nextErrors.customerEmail = 'Email or phone is required';
            nextErrors.phone = 'Email or phone is required';
        }
        if (!form.message.trim()) nextErrors.message = 'Message is required';
        setErrors(nextErrors);
        return Object.keys(nextErrors).length === 0;
    };

    const handleSubmit = async () => {
        if (!selectedOption || !validateForm()) return;

        setSubmitting(true);
        setSubmitError('');

        const subject = `${selectedOption.emailSubject} – ${serviceTitle || 'General'}`;
        const summary = buildContactSummary({
            inquiryType: selectedOption.label,
            serviceTitle,
            customerName: form.customerName.trim(),
            customerEmail: form.customerEmail.trim(),
            phone: form.phone.trim(),
            businessName: form.businessName.trim(),
            message: form.message.trim(),
        });

        try {
            await sendMailRequest({
                subject,
                summary,
                customerName: form.customerName.trim(),
                customerEmail: form.customerEmail.trim() || undefined,
                businessName: form.businessName.trim() || undefined,
                phone: form.phone.trim() || undefined,
                inquiryType: selectedOption.value,
            });
            setSubmitted(true);
            loggerHelper.info('Contact dialog message sent', {
                inquiryType: selectedOption.value,
                serviceTitle,
            });
        } catch (error) {
            setSubmitError(error.message || 'Could not send your message. Please try again.');
            loggerHelper.error('Contact dialog send failed', { message: error.message });
        } finally {
            setSubmitting(false);
        }
    };

    const renderOptions = () => (
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))' }, gap: 1.5 }}>
            {options.map((option) => (
                <Box
                    component="button"
                    type="button"
                    key={option.value}
                    onClick={() => handleOptionClick(option)}
                    sx={{
                        appearance: 'none',
                        width: '100%',
                        minHeight: 178,
                        p: 2.5,
                        borderRadius: 2.5,
                        border: '1px solid rgba(16,42,58,0.11)',
                        bgcolor: '#FBFDFD',
                        color: 'text.primary',
                        font: 'inherit',
                        textAlign: 'left',
                        cursor: 'pointer',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'flex-start',
                        transition: 'transform 180ms ease, border-color 180ms ease, box-shadow 180ms ease',
                        '&:hover': {
                            transform: 'translateY(-3px)',
                            borderColor: 'rgba(32,184,181,0.55)',
                            boxShadow: '0 16px 34px rgba(16,42,58,0.08)',
                        },
                        '&:focus-visible': {
                            outline: '3px solid rgba(32,184,181,0.28)',
                            outlineOffset: 2,
                        },
                    }}
                >
                    <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ width: '100%' }}>
                        <Box sx={{ width: 42, height: 42, borderRadius: 2, display: 'grid', placeItems: 'center', color: 'primary.main', bgcolor: 'secondary.light' }}>
                            {option.icon}
                        </Box>
                        <ArrowForwardIcon sx={{ color: 'secondary.dark', fontSize: 20 }} />
                    </Stack>
                    <Typography sx={{ mt: 2.4, fontSize: '1.08rem', lineHeight: 1.25, fontWeight: 760 }}>
                        {option.label}
                    </Typography>
                    <Typography variant="body2" sx={{ mt: 0.8, color: 'text.secondary', lineHeight: 1.55 }}>
                        {option.description}
                    </Typography>
                </Box>
            ))}
        </Box>
    );

    const renderForm = () => {
        if (submitted) {
            return (
                <Alert severity="success" sx={{ mt: 1 }}>
                    Thanks {form.customerName.trim() || 'there'} — your message was sent. We’ll get back to you soon.
                </Alert>
            );
        }

        return (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <Box sx={{ display: 'inline-flex', alignSelf: 'flex-start', px: 1.4, py: 0.7, borderRadius: 5, bgcolor: 'secondary.light', color: 'secondary.dark' }}>
                    <Typography variant="caption" fontWeight={750}>{selectedOption?.label} · {serviceTitle || 'General'}</Typography>
                </Box>
                <TextField
                    label="Your name"
                    value={form.customerName}
                    onChange={(e) => updateField('customerName', e.target.value)}
                    error={Boolean(errors.customerName)}
                    helperText={errors.customerName}
                    fullWidth
                    required
                />
                <TextField
                    label="Email"
                    type="email"
                    value={form.customerEmail}
                    onChange={(e) => updateField('customerEmail', e.target.value)}
                    error={Boolean(errors.customerEmail)}
                    helperText={errors.customerEmail}
                    fullWidth
                />
                <TextField
                    label="Phone / WhatsApp"
                    value={form.phone}
                    onChange={(e) => updateField('phone', e.target.value)}
                    error={Boolean(errors.phone)}
                    helperText={errors.phone}
                    fullWidth
                />
                <TextField
                    label="Business name (optional)"
                    value={form.businessName}
                    onChange={(e) => updateField('businessName', e.target.value)}
                    fullWidth
                />
                <TextField
                    label="Message"
                    value={form.message}
                    onChange={(e) => updateField('message', e.target.value)}
                    error={Boolean(errors.message)}
                    helperText={errors.message}
                    fullWidth
                    required
                    multiline
                    minRows={5}
                />
                {submitError && <Alert severity="error">{submitError}</Alert>}
            </Box>
        );
    };

    return (
        <>
            <Dialog
                open={open}
                onClose={submitting ? undefined : onClose}
                maxWidth="sm"
                fullWidth
                PaperProps={{
                    sx: {
                        borderRadius: { xs: 0, sm: 3.5 },
                        bgcolor: 'background.paper',
                        border: '1px solid rgba(16,42,58,0.1)',
                        boxShadow: '0 32px 90px rgba(4,31,48,0.22)',
                        overflow: 'hidden',
                    },
                }}
            >
                <DialogTitle
                    sx={{
                        px: { xs: 2.5, sm: 4 },
                        pt: { xs: 2.5, sm: 3.5 },
                        pb: 2.5,
                        bgcolor: '#082F49',
                        color: 'white',
                        position: 'relative',
                    }}
                >
                    <Typography variant="overline" sx={{ color: '#84E4E1', fontWeight: 800, letterSpacing: 1.3 }}>
                        Start a conversation
                    </Typography>
                    <Typography component="div" sx={{ mt: 0.5, pr: 5, fontSize: { xs: '1.55rem', sm: '2rem' }, lineHeight: 1.15, fontWeight: 800, letterSpacing: '-0.035em' }}>
                        {selectedOption ? 'Send a message' : 'How would you like to get started?'}
                    </Typography>
                    <Typography sx={{ mt: 1.2, maxWidth: 520, color: 'rgba(255,255,255,0.64)', fontSize: '0.95rem', lineHeight: 1.6 }}>
                        {selectedOption
                            ? 'Share a few details and the DevCraft team will get back to you.'
                            : 'Choose the option that best matches what you need.'}
                    </Typography>
                    <IconButton aria-label="close dialog" onClick={onClose} disabled={submitting} sx={{ position: 'absolute', top: { xs: 18, sm: 24 }, right: { xs: 16, sm: 24 }, color: 'white', bgcolor: 'rgba(255,255,255,0.08)', '&:hover': { bgcolor: 'rgba(255,255,255,0.14)' } }}>
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>
                <DialogContent sx={{ px: { xs: 2.5, sm: 4 }, py: { xs: 2.5, sm: 3.5 } }}>
                    {selectedOption ? renderForm() : renderOptions()}
                </DialogContent>
                {selectedOption && <DialogActions
                    sx={{
                        px: { xs: 2.5, sm: 4 },
                        py: 2.2,
                        borderTop: '1px solid rgba(16,42,58,0.08)',
                        bgcolor: '#F7FBFC',
                        justifyContent: 'space-between',
                    }}
                >
                    {selectedOption && !submitted ? (
                        <Button
                            startIcon={<ArrowBackIcon />}
                            onClick={() => {
                                setSelectedOption(null);
                                setSubmitError('');
                            }}
                            disabled={submitting}
                            sx={{ px: 0 }}
                        >
                            Back
                        </Button>
                    ) : (
                        <Box />
                    )}
                    <Box sx={{ display: 'flex', gap: 1 }}>
                        {(selectedOption || submitted) && <Button onClick={onClose} variant="text" color="primary" disabled={submitting}>Close</Button>}
                        {selectedOption && !submitted && (
                            <Button
                                onClick={handleSubmit}
                                variant="contained"
                                color="primary"
                                disabled={submitting}
                                sx={{ px: 3, py: 1.15 }}
                            >
                                {submitting ? 'Sending…' : 'Send message'}
                            </Button>
                        )}
                    </Box>
                </DialogActions>}
            </Dialog>
            <CalendarDialog
                open={calendarDialogOpen}
                onClose={() => setCalendarDialogOpen(false)}
            />
        </>
    );
};

export default ContactDialog;
